import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Users, UserPlus, MoreVertical, KeyRound, Pause, Play, Trash2, Copy, Check, Loader2, ShieldAlert, Mail, Shield, Send,
} from 'lucide-react';
import { toast } from 'sonner';

type ActionType = 'suspend' | 'reactivate' | 'remove' | 'reset_password';

export function AgencyMembersPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState<'invite' | 'create'>('invite');
  const [credentials, setCredentials] = useState<{ email: string; password?: string | null; full_name: string; invite_link?: string | null; invite_sent?: boolean } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: ActionType; member: any } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [permsFor, setPermsFor] = useState<any | null>(null);
  const [editProfileFor, setEditProfileFor] = useState<any | null>(null);

  // Add form
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [socialPlatform, setSocialPlatform] = useState<string>('');
  const [socialUrl, setSocialUrl] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // Get agency
  const { data: agency } = useQuery({
    queryKey: ['my-agency-for-members', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name').eq('created_by', user!.id).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  // Members list (with their permissions)
  const { data: members, isLoading } = useQuery({
    queryKey: ['agency-members', agency?.id],
    queryFn: async () => {
      const { data: agentProfiles } = await supabase
        .from('agent_profiles').select('*').eq('agency_id', agency!.id);
      if (!agentProfiles || agentProfiles.length === 0) return [];
      const userIds = agentProfiles.map(a => a.user_id);
      const [{ data: profiles }, { data: perms }] = await Promise.all([
        supabase.from('profiles').select('*').in('user_id', userIds),
        supabase.from('agent_permissions').select('*').in('agent_user_id', userIds).eq('agency_id', agency!.id),
      ]);
      const profMap = Object.fromEntries((profiles || []).map(p => [p.user_id, p]));
      const permMap = Object.fromEntries((perms || []).map((p: any) => [p.agent_user_id, p]));
      return agentProfiles.map(a => ({ ...a, profile: profMap[a.user_id] || null, perms: permMap[a.user_id] || null }));
    },
    enabled: !!agency?.id,
  });

  const callFn = async (body: any) => {
    const { data, error } = await supabase.functions.invoke('agency-member-management', { body });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const uploadPhoto = async (file: File, userId: string): Promise<string | null> => {
    const ext = file.name.split('.').pop();
    const path = `${userId}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('agent-photos').upload(path, file, { upsert: true });
    if (error) { toast.error(error.message); return null; }
    return supabase.storage.from('agent-photos').getPublicUrl(path).data.publicUrl;
  };

  const handleAdd = async () => {
    if (!fullName.trim() || !email.trim()) {
      toast.error('Full name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const data = await callFn({
        action: addMode,
        agency_id: agency!.id,
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
      });

      // Apply photo + social to the just-created agent profile
      const newUserId = data?.member?.user_id;
      if (newUserId && (photoFile || socialPlatform || socialUrl || phone)) {
        let photoUrl: string | null = null;
        if (photoFile) photoUrl = await uploadPhoto(photoFile, newUserId);
        const updates: any = {};
        if (photoUrl) updates.photo_url = photoUrl;
        if (phone.trim()) updates.phone_number = phone.trim();
        if (socialPlatform) updates.social_platform = socialPlatform;
        if (socialUrl.trim()) updates.social_url = socialUrl.trim();
        if (Object.keys(updates).length > 0) {
          await supabase.from('agent_profiles').update(updates).eq('user_id', newUserId);
        }
      }

      setCredentials(data.member);
      setAddOpen(false);
      setFullName(''); setEmail(''); setPhone('');
      setSocialPlatform(''); setSocialUrl(''); setPhotoFile(null);
      qc.invalidateQueries({ queryKey: ['agency-members'] });
      toast.success(addMode === 'invite' ? 'Invite sent' : 'Member added successfully');
    } catch (e: any) {
      toast.error(e.message || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setSubmitting(true);
    try {
      const data = await callFn({
        action: confirmAction.type,
        member_user_id: confirmAction.member.user_id,
      });
      if (confirmAction.type === 'reset_password') {
        setCredentials({
          email: confirmAction.member.profile?.full_name || '',
          password: data.password,
          full_name: confirmAction.member.profile?.full_name || 'Member',
        });
      } else {
        toast.success(
          confirmAction.type === 'suspend' ? 'Member suspended' :
          confirmAction.type === 'reactivate' ? 'Member reactivated' :
          'Member removed'
        );
      }
      qc.invalidateQueries({ queryKey: ['agency-members'] });
      setConfirmAction(null);
    } catch (e: any) {
      toast.error(e.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePerm = async (member: any, key: string, value: boolean) => {
    const existing = member.perms;
    try {
      if (existing) {
        const { error } = await supabase
          .from('agent_permissions')
          .update({ [key]: value, updated_by: user!.id } as any)
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const defaults: any = {
          agency_id: agency!.id,
          agent_user_id: member.user_id,
          can_add_listings: true,
          can_edit_listings: true,
          can_view_all_leads: false,
          can_manage_clients: true,
          can_view_analytics: false,
          can_take_leads: true,
          updated_by: user!.id,
        };
        defaults[key] = value;
        const { error } = await supabase.from('agent_permissions').insert(defaults);
        if (error) throw error;
      }
      qc.invalidateQueries({ queryKey: ['agency-members'] });
      toast.success('Permission updated');
    } catch (e: any) {
      toast.error(e.message || 'Failed to update');
    }
  };

  const copyAll = () => {
    if (!credentials) return;
    const text = credentials.invite_link
      ? `Hi ${credentials.full_name},\n\nYou've been invited to join the agency. Set your password here: ${credentials.invite_link}`
      : `Email: ${credentials.email}\nPassword: ${credentials.password}`;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (!agency) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">You must own a verified agency to manage members.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" /> Team Members
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Invite, suspend, or remove agents in <span className="font-medium text-foreground">{agency.name}</span>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setAddMode('create'); setAddOpen(true); }} className="gap-2">
            <UserPlus className="h-4 w-4" /> Add with Password
          </Button>
          <Button onClick={() => { setAddMode('invite'); setAddOpen(true); }} className="gap-2">
            <Send className="h-4 w-4" /> Send Invite
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-2">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : !members || members.length === 0 ? (
            <div className="p-10 text-center">
              <Users className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No team members yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Click "Send Invite" to onboard your first agent.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Lead Taking</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m: any) => {
                  const active = m.profile?.is_active !== false;
                  const takesLeads = m.perms?.can_take_leads !== false;
                  return (
                    <TableRow key={m.id}>
                      <TableCell>
                        <div className="font-medium text-sm">{m.profile?.full_name || 'Unnamed'}</div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{m.profile?.phone || '—'}</TableCell>
                      <TableCell>
                        {active ? (
                          <Badge className="bg-emerald-500/15 text-emerald-700 border-0">Active</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-500/15 text-amber-700 border-0">Suspended</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={takesLeads ? 'default' : 'secondary'} className={takesLeads ? '' : 'bg-muted text-muted-foreground'}>
                          {takesLeads ? 'ON' : 'OFF'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(m.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => setPermsFor(m)}>
                              <Shield className="h-4 w-4 mr-2" /> Permissions
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConfirmAction({ type: 'reset_password', member: m })}>
                              <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                            </DropdownMenuItem>
                            {active ? (
                              <DropdownMenuItem onClick={() => setConfirmAction({ type: 'suspend', member: m })}>
                                <Pause className="h-4 w-4 mr-2" /> Suspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => setConfirmAction({ type: 'reactivate', member: m })}>
                                <Play className="h-4 w-4 mr-2" /> Reactivate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setConfirmAction({ type: 'remove', member: m })}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Invite Member Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{addMode === 'invite' ? 'Send Invite Link' : 'Add Member with Password'}</DialogTitle>
            <DialogDescription>
              {addMode === 'invite'
                ? 'A secure link will be emailed to the agent so they can set their own password.'
                : "A login account will be created. You'll see the password once — copy and share it securely."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Full Name *</Label>
              <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Doe" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@example.com" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+251 9..." />
            </div>
            <div>
              <Label>Photo</Label>
              <Input type="file" accept="image/*" onChange={e => setPhotoFile(e.target.files?.[0] || null)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Social Platform</Label>
                <select
                  className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                  value={socialPlatform}
                  onChange={e => setSocialPlatform(e.target.value)}
                >
                  <option value="">None</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="telegram">Telegram</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="tiktok">TikTok</option>
                  <option value="x">X (Twitter)</option>
                </select>
              </div>
              <div>
                <Label>Social URL</Label>
                <Input value={socialUrl} onChange={e => setSocialUrl(e.target.value)} placeholder="https://..." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {addMode === 'invite' ? 'Send Invite' : 'Create Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials / invite link shown once */}
      <Dialog open={!!credentials} onOpenChange={(o) => !o && setCredentials(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-500" />
              {credentials?.invite_link ? 'Invite Created' : 'Credentials Generated'}
            </DialogTitle>
            <DialogDescription>
              {credentials?.invite_link
                ? `${credentials.invite_sent ? 'An email was sent. ' : ''}You can also share this one-time link with ${credentials.full_name}.`
                : `These credentials will only be shown once. Copy and share them securely with ${credentials?.full_name}.`}
            </DialogDescription>
          </DialogHeader>
          {credentials && (
            <div className="space-y-3 bg-muted/50 rounded-lg p-4">
              <div>
                <Label className="text-xs">Email</Label>
                <div className="font-mono text-sm bg-background rounded p-2 mt-1">{credentials.email}</div>
              </div>
              {credentials.invite_link ? (
                <div>
                  <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Invite Link</Label>
                  <div className="font-mono text-xs bg-background rounded p-2 mt-1 break-all select-all">{credentials.invite_link}</div>
                </div>
              ) : credentials.password ? (
                <div>
                  <Label className="text-xs">Temporary Password</Label>
                  <div className="font-mono text-sm bg-background rounded p-2 mt-1 select-all">{credentials.password}</div>
                </div>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button onClick={copyAll} variant="outline" className="gap-2">
              <Copy className="h-4 w-4" /> Copy
            </Button>
            <Button onClick={() => setCredentials(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions sheet */}
      <Sheet open={!!permsFor} onOpenChange={(o) => !o && setPermsFor(null)}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Permissions</SheetTitle>
            <SheetDescription>{permsFor?.profile?.full_name}</SheetDescription>
          </SheetHeader>
          {permsFor && (
            <div className="space-y-4 mt-4">
              {[
                { key: 'can_take_leads', label: 'Take Leads', desc: 'Include this agent in auto-assignment / claim pool' },
                { key: 'can_add_listings', label: 'Add Listings', desc: 'Can create new properties / plots' },
                { key: 'can_edit_listings', label: 'Edit Listings', desc: 'Can edit existing listings' },
                { key: 'can_view_all_leads', label: 'View All Leads', desc: 'See agency-wide leads, not only their own' },
                { key: 'can_manage_clients', label: 'Manage Clients', desc: 'Access client management module' },
                { key: 'can_view_analytics', label: 'View Analytics', desc: 'Access agency analytics' },
              ].map(p => {
                const current = permsFor.perms ? permsFor.perms[p.key] : (p.key === 'can_view_all_leads' || p.key === 'can_view_analytics' ? false : true);
                return (
                  <div key={p.key} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-muted/40">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{p.label}</div>
                      <div className="text-xs text-muted-foreground">{p.desc}</div>
                    </div>
                    <Switch
                      checked={!!current}
                      onCheckedChange={(v) => togglePerm(permsFor, p.key, v)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Confirm action */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'suspend' && 'Suspend this member?'}
              {confirmAction?.type === 'reactivate' && 'Reactivate this member?'}
              {confirmAction?.type === 'remove' && 'Remove this member?'}
              {confirmAction?.type === 'reset_password' && 'Reset password?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'suspend' && 'They will be unable to log in until reactivated.'}
              {confirmAction?.type === 'reactivate' && 'They will regain access to their dashboard.'}
              {confirmAction?.type === 'remove' && 'They will lose agent access and be unlinked from your agency. This cannot be undone easily.'}
              {confirmAction?.type === 'reset_password' && 'A new temporary password will be generated and shown once.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={submitting}
              className={confirmAction?.type === 'remove' ? 'bg-destructive hover:bg-destructive/90' : ''}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
